import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  User, 
  GraduationCap, 
  Calendar, 
  CreditCard, 
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  DollarSign,
  ArrowRight,
  Lock,
  Eye,
  EyeOff,
  Settings
} from 'lucide-react';
import ReenrollmentCard from '../components/ReenrollmentCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function AccountSettings({ user, language }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
      toast.success(language === 'es' ? 'Contraseña actualizada' : 'Password updated');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'es' ? 'Error al cambiar contraseña' : 'Error changing password'));
    }
    setSaving(false);
  };

  return (
    <div className="max-w-2xl space-y-8" data-testid="account-settings">
      {/* Profile Info */}
      <div className="card-apple p-8">
        <h2 className="text-xl font-semibold text-[#1D1D1F] flex items-center gap-2 mb-6">
          <User className="h-6 w-6 text-[#E63946]" />
          {language === 'es' ? 'Mi Perfil' : 'My Profile'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-xs text-[#86868B]">{language === 'es' ? 'Nombre' : 'Name'}</Label>
            <p className="text-[#1D1D1F] font-medium" data-testid="profile-name">{user?.name || '—'}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#86868B]">Email</Label>
            <p className="text-[#1D1D1F] font-medium" data-testid="profile-email">{user?.email || '—'}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#86868B]">{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
            <p className="text-[#1D1D1F]">{user?.phone || '—'}</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-[#86868B]">{language === 'es' ? 'Rol' : 'Role'}</Label>
            <p className="text-[#1D1D1F] capitalize">{user?.role || '—'}</p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="card-apple p-8">
        <h2 className="text-xl font-semibold text-[#1D1D1F] flex items-center gap-2 mb-6">
          <Lock className="h-6 w-6 text-[#E63946]" />
          {language === 'es' ? 'Cambiar Contraseña' : 'Change Password'}
        </h2>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="space-y-2">
            <Label>{language === 'es' ? 'Contraseña actual' : 'Current password'}</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#86868B]" />
              <Input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="pl-11 pr-11 py-5 rounded-xl" required data-testid="current-password-input" />
              <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#86868B]">
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{language === 'es' ? 'Nueva contraseña' : 'New password'}</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#86868B]" />
              <Input type={showNew ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="pl-11 pr-11 py-5 rounded-xl" required minLength={6} data-testid="new-password-input" />
              <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#86868B]">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{language === 'es' ? 'Confirmar nueva contraseña' : 'Confirm new password'}</Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#86868B]" />
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="pl-11 py-5 rounded-xl" required minLength={6} data-testid="confirm-password-input" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="bg-[#E63946] hover:bg-[#D62828] rounded-xl py-5 w-full" data-testid="change-password-btn">
            {saving ? '...' : (language === 'es' ? 'Cambiar Contraseña' : 'Change Password')}
          </Button>
        </form>
      </div>
    </div>
  );
}


export default function DashboardPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [selectedYear, setSelectedYear] = useState('2026-2027');

  const getLevelName = (code) => {
    if (!code) return '—';
    const mapping = {
      'infantil_3': 'Ed. Infantil 3 años', 'infantil_4': 'Ed. Infantil 4 años', 'infantil_5': 'Ed. Infantil 5 años',
      'primaria_1': 'Primaria 1°', 'primaria_2': 'Primaria 2°', 'primaria_3': 'Primaria 3°',
      'primaria_4': 'Primaria 4°', 'primaria_5': 'Primaria 5°', 'primaria_6': 'Primaria 6°',
      'eso_1': 'ESO 1°', 'eso_2': 'ESO 2°', 'eso_3': 'ESO 3°', 'eso_4': 'ESO 4°',
      'bachillerato_1': 'Bachillerato 1°', 'bachillerato_2': 'Bachillerato 2°',
      'level_a1': 'Nivel A1', 'level_a2': 'Nivel A2', 'level_b1': 'Nivel B1', 'level_b2': 'Nivel B2',
      'spanish_kids': 'Español Infantil', 'spanish_teens': 'Español Adolescentes',
      'beginner': 'Principiante', 'basic': 'Basico', 'intermediate': 'Intermedio',
      'advanced': 'Avanzado', 'native': 'Nativo / Herencia',
    };
    if (mapping[code]) return mapping[code];
    if (code.startsWith('course_')) return 'Curso asignado';
    return code.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  useEffect(() => {
    if (user?.role !== 'teacher') fetchData();
    checkPaymentReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPaymentReturn = () => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    if (paymentStatus === 'success') {
      toast.success(language === 'es' ? '¡Pago recibido! Tu inscripción está confirmada.' : 'Payment received! Your enrollment is confirmed.');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (paymentStatus === 'canceled') {
      toast.error(language === 'es' ? 'El pago fue cancelado. Puedes intentar de nuevo.' : 'Payment was canceled. You can try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  if (user?.role === 'teacher') {
    return <Navigate to="/teacher" replace />;
  }

  const fetchData = async () => {
    try {
      const [studentsRes, enrollmentsRes, paymentsRes] = await Promise.all([
        axios.get(`${API}/students`, { withCredentials: true }),
        axios.get(`${API}/enrollments`, { withCredentials: true }),
        axios.get(`${API}/parent/my-payments`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      
      let studentsList = studentsRes.data || [];
      const enrollmentsList = enrollmentsRes.data || [];
      
      // If no students found but enrollments exist, build students from enrollments
      if (studentsList.length === 0 && enrollmentsList.length > 0) {
        const seen = new Set();
        studentsList = enrollmentsList
          .filter(e => e.student_name && !seen.has(e.student_name) && seen.add(e.student_name))
          .map(e => ({
            student_id: e.student_id || e.enrollment_id,
            name: e.student_name,
            current_grade: e.student_grade || '',
            spanish_level: e.spanish_level || e.selected_course || '',
            enrolled_courses: e.selected_course ? [e.selected_course] : []
          }));
      }
      
      setStudents(studentsList);
      setEnrollments(enrollmentsList);
      if (paymentsRes.data) setPaymentData(paymentsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter enrollments by selected year
  const filteredEnrollments = enrollments.filter(e => e.school_year === selectedYear);
  const isViewing2627 = selectedYear === '2026-2027';


  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
      case 'paid':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'paid':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'rejected':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <Navbar />
      
      <main className="pt-36 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl font-semibold text-[#1D1D1F] mb-2" data-testid="dashboard-title">
              {language === 'es' ? `Hola, ${user?.name?.split(' ')[0]}` : `Hello, ${user?.name?.split(' ')[0]}`}
            </h1>
            <p className="text-[#86868B]">
              {language === 'es' ? 'Bienvenido a tu panel de control' : 'Welcome to your dashboard'}
            </p>
            {/* Tabs */}
            <div className="flex gap-2 mt-6">
              {/* Year Selector */}
              <div className="flex items-center bg-white rounded-full p-1 mr-4 border border-black/5" data-testid="year-selector">
                {['2025-2026', '2026-2027'].map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      selectedYear === year 
                        ? 'bg-[#1D1D1F] text-white shadow-sm' 
                        : 'text-[#86868B] hover:text-[#1D1D1F]'
                    }`}
                    data-testid={`year-btn-${year}`}
                  >
                    {year}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeTab === 'overview' ? 'bg-[#E63946] text-white' : 'bg-white text-[#86868B] hover:bg-[#F5F5F7]'}`}
                data-testid="tab-overview"
              >
                {language === 'es' ? 'Resumen' : 'Overview'}
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'payments' ? 'bg-[#E63946] text-white' : 'bg-white text-[#86868B] hover:bg-[#F5F5F7]'}`}
                data-testid="tab-payments"
              >
                <DollarSign className="h-4 w-4" />
                {language === 'es' ? 'Mis Pagos' : 'My Payments'}
              </button>
              <button
                onClick={() => setActiveTab('account')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'account' ? 'bg-[#E63946] text-white' : 'bg-white text-[#86868B] hover:bg-[#F5F5F7]'}`}
                data-testid="tab-account"
              >
                <Settings className="h-4 w-4" />
                {language === 'es' ? 'Mi Cuenta' : 'My Account'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
            </div>
          ) : activeTab === 'payments' ? (
            /* ======= MIS PAGOS TAB ======= */
            (() => {
              // Filter payment data by selected year
              const yearEnrollments = (paymentData?.enrollments || []).filter(e => e.school_year === selectedYear);
              const yearPaid = yearEnrollments.reduce((sum, e) => sum + (e.amount_paid || 0), 0);
              const yearRemaining = yearEnrollments.reduce((sum, e) => sum + ((e.total_amount || 0) - (e.amount_paid || 0)), 0);
              const yearActive = yearEnrollments.filter(e => ['active', 'approved', 'awaiting_payment', 'pending_payment', 'ingresado'].includes(e.status));
              
              return (
            <div className="space-y-8" data-testid="my-payments-section">
              {/* Payment Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card-apple p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#86868B]">{language === 'es' ? 'Total Pagado' : 'Total Paid'}</p>
                      <p className="text-3xl font-bold text-[#34C759]">${yearPaid.toLocaleString()}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#34C759]/10 flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-[#34C759]" />
                    </div>
                  </div>
                </div>
                <div className="card-apple p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#86868B]">{language === 'es' ? 'Saldo Pendiente' : 'Remaining Balance'}</p>
                      <p className="text-3xl font-bold text-[#FF9500]">${yearRemaining.toLocaleString()}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#FF9500]/10 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-[#FF9500]" />
                    </div>
                  </div>
                </div>
                <div className="card-apple p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#86868B]">{language === 'es' ? 'Planes Activos' : 'Active Plans'}</p>
                      <p className="text-3xl font-bold text-[#007AFF]">{yearActive.length}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#007AFF]/10 flex items-center justify-center">
                      <CreditCard className="h-6 w-6 text-[#007AFF]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Enrollments/Plans */}
              <div className="card-apple p-8">
                <h2 className="text-xl font-semibold text-[#1D1D1F] flex items-center gap-2 mb-6">
                  <CreditCard className="h-6 w-6 text-[#E63946]" />
                  {language === 'es' ? 'Mis Planes' : 'My Plans'}
                </h2>
                {yearEnrollments.length === 0 ? (
                  <div className="text-center py-8">
                    <CreditCard className="h-12 w-12 text-[#86868B] mx-auto mb-4" />
                    <p className="text-[#86868B]">{language === 'es' ? 'No hay inscripciones para este año' : 'No enrollments for this year'}</p>
                    {isViewing2627 && (
                      <button onClick={() => setActiveTab('overview')} className="text-[#E63946] font-medium hover:underline mt-2">
                        {language === 'es' ? 'Inscribir para 2026-2027' : 'Enroll for 2026-2027'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {yearEnrollments.map((e) => (
                      <div key={e.enrollment_id} className="p-5 bg-[#F5F5F7] rounded-xl" data-testid={`plan-card-${e.enrollment_id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <p className="font-semibold text-[#1D1D1F]">{e.student_name}</p>
                            <p className="text-xs text-[#86868B]">
                              {e.enrollment_type === 'annual' ? (language === 'es' ? 'Plan Anual' : 'Annual Plan') : (language === 'es' ? 'Plan Trimestral' : 'Trimester Plan')}
                              {' — '}{e.pricing_tier === 'early_bird' ? 'Early Bird' : e.pricing_tier === 'normal' ? 'Normal' : 'Trimestral'}
                            </p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(e.status)}`}>
                            {e.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-[#86868B]">{language === 'es' ? 'Total' : 'Total'}</p>
                            <p className="font-semibold text-[#1D1D1F]">${e.total_amount?.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[#86868B]">{language === 'es' ? 'Pagado' : 'Paid'}</p>
                            <p className="font-semibold text-[#34C759]">${(e.amount_paid || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[#86868B]">{language === 'es' ? 'Pendiente' : 'Remaining'}</p>
                            <p className="font-semibold text-[#FF9500]">${(e.remaining_balance || e.total_amount || 0).toLocaleString()}</p>
                          </div>
                        </div>
                        {/* Progress bar */}
                        {e.total_amount > 0 && (
                          <div className="mt-3">
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-[#34C759] rounded-full transition-all"
                                style={{ width: `${Math.min(100, ((e.amount_paid || 0) / e.total_amount) * 100)}%` }}
                              />
                            </div>
                            <p className="text-xs text-[#86868B] mt-1 text-right">
                              {Math.round(((e.amount_paid || 0) / e.total_amount) * 100)}% {language === 'es' ? 'pagado' : 'paid'}
                            </p>
                          </div>
                        )}
                        {e.payment_plan && e.payment_plan !== 'full' && (
                          <div className="mt-3 pt-3 border-t border-black/5 flex items-center justify-between text-xs">
                            <span className="text-[#86868B]">
                              {e.payment_plan === 'two_installments' ? (language === 'es' ? '2 cuotas' : '2 installments') :
                               e.payment_plan === 'monthly' ? (language === 'es' ? '10 mensualidades' : '10 monthly') :
                               (language === 'es' ? 'Pago unico' : 'Single payment')}
                            </span>
                            {e.next_payment_date && (
                              <span className="text-[#86868B]">
                                {language === 'es' ? 'Próximo pago:' : 'Next payment:'} {new Date(e.next_payment_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment History */}
              <div className="card-apple p-8">
                <h2 className="text-xl font-semibold text-[#1D1D1F] flex items-center gap-2 mb-6">
                  <DollarSign className="h-6 w-6 text-[#E63946]" />
                  {language === 'es' ? 'Historial de Pagos' : 'Payment History'}
                </h2>
                {(paymentData?.payments || []).length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 text-[#86868B] mx-auto mb-4" />
                    <p className="text-[#86868B]">{language === 'es' ? 'No hay pagos registrados' : 'No payments recorded'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-black/5">
                          <th className="text-left py-3 font-medium text-[#86868B]">{language === 'es' ? 'Fecha' : 'Date'}</th>
                          <th className="text-left py-3 font-medium text-[#86868B]">{language === 'es' ? 'Tipo' : 'Type'}</th>
                          <th className="text-right py-3 font-medium text-[#86868B]">{language === 'es' ? 'Monto' : 'Amount'}</th>
                          <th className="text-right py-3 font-medium text-[#86868B]">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(paymentData?.payments || []).map((p) => (
                          <tr key={p.payment_id} className="border-b border-black/5" data-testid={`payment-row-${p.payment_id}`}>
                            <td className="py-3 text-[#1D1D1F]">{new Date(p.created_at).toLocaleDateString()}</td>
                            <td className="py-3 text-[#86868B] capitalize">{p.payment_type}</td>
                            <td className={`py-3 text-right font-semibold ${p.amount < 0 ? 'text-[#E63946]' : 'text-[#34C759]'}`}>
                              {p.amount < 0 ? '-' : ''}${Math.abs(p.amount).toLocaleString()}
                            </td>
                            <td className="py-3 text-right">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${p.status === 'succeeded' ? 'bg-green-100 text-green-700' : p.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                                {p.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
              );
            })()
          ) : activeTab === 'account' ? (
            /* ======= MI CUENTA TAB ======= */
            <AccountSettings user={user} language={language} />
          ) : (
            /* ======= OVERVIEW TAB (existing content) ======= */
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-8">
                {/* Students */}
                <div className="card-apple p-8">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-[#1D1D1F] flex items-center gap-2">
                      <GraduationCap className="h-6 w-6 text-[#E63946]" />
                      {language === 'es' ? 'Mis Hijos' : 'My Children'}
                    </h2>
                    <Link to="/enrollment">
                      <Button variant="outline" size="sm" className="rounded-full" data-testid="add-child-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Añadir hijo' : 'Add child'}
                      </Button>
                    </Link>
                  </div>

                  {students.length === 0 ? (
                    <div className="text-center py-8">
                      <GraduationCap className="h-12 w-12 text-[#86868B] mx-auto mb-4" />
                      <p className="text-[#86868B]">
                        {language === 'es' 
                          ? 'No tienes hijos inscritos todavía'
                          : 'You don\'t have any enrolled children yet'}
                      </p>
                      <Link to="/enrollment" className="text-[#E63946] font-medium hover:underline">
                        {language === 'es' ? 'Inscribir ahora' : 'Enroll now'}
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {students.map((student) => {
                        const studentEnrollment = filteredEnrollments.find(e => 
                          e.student_name === student.name || e.student_id === student.student_id
                        );
                        return (
                          <div 
                            key={student.student_id}
                            className="p-4 bg-[#F5F5F7] rounded-xl cursor-pointer hover:bg-[#EBEBEF] transition-colors"
                            onClick={() => studentEnrollment && setSelectedEnrollment(studentEnrollment)}
                            data-testid={`student-card-${student.student_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-[#E63946]/10 flex items-center justify-center">
                                  <User className="h-6 w-6 text-[#E63946]" />
                                </div>
                                <div>
                                  <p className="font-semibold text-[#1D1D1F]">{student.name}</p>
                                  <p className="text-sm text-[#86868B]">{student.current_grade}</p>
                                </div>
                              </div>
                              <span className="px-3 py-1 bg-[#E63946]/10 text-[#E63946] text-sm font-medium rounded-full">
                                {student.spanish_level}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Enrollments */}
                <div className="card-apple p-8">
                  <h2 className="text-xl font-semibold text-[#1D1D1F] flex items-center gap-2 mb-6">
                    <CreditCard className="h-6 w-6 text-[#E63946]" />
                    {language === 'es' ? 'Inscripciones' : 'Enrollments'}
                  </h2>

                  {filteredEnrollments.length === 0 ? (
                    <div className="text-center py-8">
                      <CreditCard className="h-12 w-12 text-[#86868B] mx-auto mb-4" />
                      <p className="text-[#86868B]">
                        {language === 'es' 
                          ? 'No tienes inscripciones pendientes'
                          : 'You don\'t have any enrollments'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredEnrollments.map((enrollment) => (
                        <div 
                          key={enrollment.enrollment_id}
                          className="p-4 bg-[#F5F5F7] rounded-xl cursor-pointer hover:bg-[#EBEBEF] transition-colors"
                          onClick={() => setSelectedEnrollment(enrollment)}
                          data-testid={`enrollment-card-${enrollment.enrollment_id}`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-[#1D1D1F]">{enrollment.student_name}</p>
                            <span className={`px-3 py-1 text-sm font-medium rounded-full flex items-center gap-1 ${getStatusColor(enrollment.status)}`}>
                              {getStatusIcon(enrollment.status)}
                              {enrollment.status}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-[#86868B]">{enrollment.school_year}</span>
                            <span className="font-semibold text-[#1D1D1F]">
                              ${enrollment.total_amount?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ======= RE-ENROLLMENT 26/27 ======= */}
                {isViewing2627 && <ReenrollmentCard />}

                {/* Trimester Re-enrollment */}
                {filteredEnrollments.some(e => e.status === 'active' && (e.enrollment_type === 'trimestral' || e.payment_plan === 'trimestral_single')) && (
                  <div className="card-apple p-6 border-l-4 border-[#007AFF]" data-testid="trimester-reenroll-section">
                    <h3 className="text-lg font-semibold text-[#1D1D1F] mb-2">
                      {language === 'es' ? 'Reinscripcion Trimestral' : 'Trimester Re-enrollment'}
                    </h3>
                    <p className="text-sm text-[#86868B] mb-4">
                      {language === 'es'
                        ? 'Inscribe a tu hijo/a para el próximo trimestre.'
                        : 'Enroll your child for the next trimester.'}
                    </p>
                    <Link
                      to="/enrollment?tier=trimestral"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#007AFF] text-white rounded-xl text-sm font-medium hover:bg-[#0056CC] transition-colors"
                      data-testid="trimester-reenroll-btn"
                    >
                      {language === 'es' ? 'Inscribir próximo trimestre' : 'Enroll next trimester'}
                    </Link>
                  </div>
                )}

                {/* General re-enrollment CTA */}
                <div className="card-apple p-6 bg-gradient-to-r from-[#E63946]/5 to-transparent" data-testid="reenroll-cta">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-[#1D1D1F]">
                        {language === 'es' ? 'Inscribir otro alumno' : 'Enroll another student'}
                      </h3>
                      <p className="text-sm text-[#86868B]">
                        {language === 'es' ? '20% de descuento por hermano' : '20% sibling discount'}
                      </p>
                    </div>
                    <Link
                      to="/enrollment"
                      className="px-5 py-2.5 bg-[#E63946] text-white rounded-xl text-sm font-medium hover:bg-[#D62828] transition-colors"
                      data-testid="enroll-new-btn"
                    >
                      {language === 'es' ? 'Inscribir' : 'Enroll'}
                    </Link>
                  </div>
                </div>

              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Profile Card */}
                <div className="card-apple p-8">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-[#E63946]/10 flex items-center justify-center mx-auto mb-4">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        <User className="h-10 w-10 text-[#E63946]" />
                      )}
                    </div>
                    <h3 className="font-semibold text-[#1D1D1F] mb-1">{user?.name}</h3>
                    <p className="text-sm text-[#86868B]">{user?.email}</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="card-apple p-8">
                  <h3 className="font-semibold text-[#1D1D1F] mb-4">
                    {language === 'es' ? 'Acciones rápidas' : 'Quick Actions'}
                  </h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => setActiveTab('payments')}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F5F7] transition-colors w-full text-left"
                      data-testid="quick-action-payments"
                    >
                      <DollarSign className="h-5 w-5 text-[#E63946]" />
                      <span className="text-sm">{language === 'es' ? 'Mis Pagos' : 'My Payments'}</span>
                      <ArrowRight className="h-4 w-4 text-[#86868B] ml-auto" />
                    </button>
                    <Link 
                      to="/enrollment"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F5F7] transition-colors"
                      data-testid="quick-action-enroll"
                    >
                      <Plus className="h-5 w-5 text-[#E63946]" />
                      <span className="text-sm">{language === 'es' ? 'Nueva inscripción' : 'New enrollment'}</span>
                    </Link>
                    <Link 
                      to="/courses"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F5F7] transition-colors"
                      data-testid="quick-action-courses"
                    >
                      <GraduationCap className="h-5 w-5 text-[#E63946]" />
                      <span className="text-sm">{language === 'es' ? 'Ver cursos' : 'View courses'}</span>
                    </Link>
                    <Link 
                      to="/contact"
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F5F5F7] transition-colors"
                      data-testid="quick-action-contact"
                    >
                      <Calendar className="h-5 w-5 text-[#E63946]" />
                      <span className="text-sm">{language === 'es' ? 'Contactar' : 'Contact us'}</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Enrollment Detail Modal */}
      {selectedEnrollment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedEnrollment(null)}>
          <div 
            className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(ev) => ev.stopPropagation()}
            data-testid="enrollment-detail-modal"
          >
            <div className="p-6 border-b border-black/5 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
              <h2 className="text-lg font-semibold text-[#1D1D1F]">
                {language === 'es' ? 'Detalle de inscripción' : 'Enrollment Detail'}
              </h2>
              <button onClick={() => setSelectedEnrollment(null)} className="text-[#86868B] hover:text-[#1D1D1F] text-xl font-bold">
                &times;
              </button>
            </div>
            <div className="p-6 space-y-5">
              {/* Student */}
              <div>
                <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-2">
                  {language === 'es' ? 'Estudiante' : 'Student'}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E63946]/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-[#E63946]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1D1D1F]">{selectedEnrollment.student_name}</p>
                    <p className="text-sm text-[#86868B]">{selectedEnrollment.school_year}</p>
                  </div>
                </div>
              </div>

              {/* Level & Course */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-[#F5F5F7] rounded-xl">
                  <p className="text-xs text-[#86868B] mb-1">{language === 'es' ? 'Nivel' : 'Level'}</p>
                  <p className="font-semibold text-[#1D1D1F] capitalize">{getLevelName(selectedEnrollment.spanish_level || selectedEnrollment.assigned_level)}</p>
                </div>
                <div className="p-3 bg-[#F5F5F7] rounded-xl">
                  <p className="text-xs text-[#86868B] mb-1">{language === 'es' ? 'Curso' : 'Course'}</p>
                  <p className="font-semibold text-[#1D1D1F] capitalize">{getLevelName(selectedEnrollment.selected_course)}</p>
                </div>
              </div>

              {/* Status */}
              <div className="p-3 bg-[#F5F5F7] rounded-xl flex items-center justify-between">
                <p className="text-sm text-[#86868B]">Status</p>
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(selectedEnrollment.status)}`}>
                  {selectedEnrollment.status}
                </span>
              </div>

              {/* Payment Details */}
              <div>
                <p className="text-xs font-semibold text-[#86868B] uppercase tracking-wide mb-3">
                  {language === 'es' ? 'Pago' : 'Payment'}
                </p>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#86868B]">{language === 'es' ? 'Plan' : 'Plan'}</span>
                    <span className="font-medium text-[#1D1D1F]">
                      {selectedEnrollment.payment_plan === 'full' ? (language === 'es' ? 'Pago unico' : 'Full payment') :
                       selectedEnrollment.payment_plan === 'two_installments' ? '2 cuotas' :
                       selectedEnrollment.payment_plan === 'monthly' ? '10 mensualidades' :
                       selectedEnrollment.payment_plan === 'trimestral_single' ? 'Trimestral' : selectedEnrollment.payment_plan}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#86868B]">{language === 'es' ? 'Tarifa' : 'Tier'}</span>
                    <span className="font-medium text-[#1D1D1F]">
                      {selectedEnrollment.pricing_tier === 'early_bird' ? 'Early Bird' : selectedEnrollment.pricing_tier === 'normal' ? 'Normal' : 'Trimestral'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#86868B]">Total</span>
                    <span className="font-bold text-[#1D1D1F]">${selectedEnrollment.total_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#86868B]">{language === 'es' ? 'Pagado' : 'Paid'}</span>
                    <span className="font-semibold text-[#34C759]">${(selectedEnrollment.amount_paid || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#86868B]">{language === 'es' ? 'Pendiente' : 'Remaining'}</span>
                    <span className="font-semibold text-[#FF9500]">${((selectedEnrollment.total_amount || 0) - (selectedEnrollment.amount_paid || 0)).toLocaleString()}</span>
                  </div>
                  {/* Progress */}
                  {selectedEnrollment.total_amount > 0 && (
                    <div className="pt-2">
                      <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#34C759] rounded-full transition-all"
                          style={{ width: `${Math.min(100, ((selectedEnrollment.amount_paid || 0) / selectedEnrollment.total_amount) * 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-[#86868B] mt-1 text-right">
                        {Math.round(((selectedEnrollment.amount_paid || 0) / selectedEnrollment.total_amount) * 100)}% {language === 'es' ? 'pagado' : 'paid'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contract */}
              <div className="p-3 bg-[#F5F5F7] rounded-xl">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#86868B]">{language === 'es' ? 'Contrato' : 'Contract'}</p>
                  {(selectedEnrollment.contract_signed || selectedEnrollment.status === 'active') ? (
                    <span className="flex items-center gap-1 text-[#34C759] text-sm font-medium">
                      <CheckCircle className="h-4 w-4" /> {language === 'es' ? 'Firmado' : 'Signed'}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[#FF9500] text-sm font-medium">
                      <Clock className="h-4 w-4" /> {language === 'es' ? 'Pendiente' : 'Pending'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
